import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { 
  Plus, 
  FileText, 
  Database,
  ArrowRight
} from "lucide-react";

export default function AddQuestionModal({
  isOpen,
  onClose,
  onAddManually,
  onAddFromQuestionBank
}) {
  const [selectedOption, setSelectedOption] = useState(null);

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
  };

  const handleProceed = () => {
    if (selectedOption === 'manual') {
      onAddManually();
      onClose();
    } else if (selectedOption === 'questionBank') {
      onAddFromQuestionBank();
      onClose();
    }
  };

  const handleCancel = () => {
    setSelectedOption(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-blue-600" />
            Add Questions to Exam
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Choose how you would like to add questions to your exam:
          </p>

          <div className="grid grid-cols-2 gap-3">
            {/* Add Question Manually Option */}
            <div 
              className={`cursor-pointer p-4 border-2 rounded-lg transition-all duration-200 hover:shadow-sm ${
                selectedOption === 'manual' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => handleOptionSelect('manual')}
            >
              <div className="text-center space-y-3">
                <div className={`mx-auto w-12 h-12 rounded-lg flex items-center justify-center ${
                  selectedOption === 'manual' 
                    ? 'bg-blue-100 text-blue-600' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  <FileText className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Add Question Manually</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Create custom questions from scratch with your own content, options, and difficulty levels.
                  </p>
                </div>
              </div>
            </div>

            {/* Question Bank Option */}
            <div 
              className={`cursor-pointer p-4 border-2 rounded-lg transition-all duration-200 hover:shadow-sm ${
                selectedOption === 'questionBank' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => handleOptionSelect('questionBank')}
            >
              <div className="text-center space-y-3">
                <div className={`mx-auto w-12 h-12 rounded-lg flex items-center justify-center ${
                  selectedOption === 'questionBank' 
                    ? 'bg-blue-100 text-blue-600' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  <Database className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Question Bank</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Select from pre-existing questions in your question bank organized by categories and skills.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button 
            onClick={handleProceed}
            disabled={!selectedOption}
            className="flex items-center gap-2"
          >
            Proceed
            <ArrowRight className="h-4 w-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}